import React, { useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";

/**
 * Branded QR code for a cafe partner page.
 *  - High error-correction (level H) so the centered siren logo doesn't break scanning
 *  - `excavate` punches a clear square through the QR matrix behind the logo
 *  - `mode` toggles between brand-dark (deep navy + pearl) and ink-saver light (cream + navy)
 *  - Download button extracts the rendered canvas as a PNG sized at `downloadSize`
 */
export default function CafeQR({
  url,
  size = 200,
  downloadSize = 1024,
  filename = "soma-lixir-qr.png",
  mode = "dark", // "dark" | "light"
  showDownloadBtn = true,
  testId = "cafe-qr",
}) {
  const wrapRef = useRef(null);
  const bg = mode === "light" ? "#F4ECD8" : "#0B142A"; // cream paper vs deep navy
  const fg = mode === "light" ? "#0B142A" : "#E8EEF5"; // deep navy vs pearl
  const borderClr = mode === "light" ? "rgba(11, 20, 42, 0.32)" : "rgba(143, 196, 224, 0.32)";

  const handleDownload = () => {
    const visibleCanvas = wrapRef.current?.querySelector("canvas");
    if (!visibleCanvas) return;
    const out = document.createElement("canvas");
    out.width = downloadSize;
    out.height = downloadSize;
    const ctx = out.getContext("2d");
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, downloadSize, downloadSize);
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(visibleCanvas, 0, 0, downloadSize, downloadSize);
    const dataUrl = out.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div data-testid={testId} ref={wrapRef} style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
      <div style={{
        padding: 12,
        background: bg,
        border: `1px solid ${borderClr}`,
        boxShadow: mode === "light"
          ? "0 6px 18px rgba(0, 0, 0, 0.25)"
          : "0 6px 18px rgba(3, 8, 23, 0.5)",
      }}>
        <QRCodeCanvas
          value={url}
          size={size}
          level="H"
          bgColor={bg}
          fgColor={fg}
          marginSize={2}
          imageSettings={{
            src: "/siren-cup.png",
            height: Math.round(size * 0.22),
            width: Math.round(size * 0.22),
            excavate: true,
          }}
        />
      </div>
      {showDownloadBtn && (
        <button
          type="button"
          onClick={handleDownload}
          data-testid={`${testId}-download`}
          style={{
            background: "transparent",
            border: "1px solid var(--terracotta-soft)",
            color: "var(--terracotta-soft)",
            padding: "8px 16px",
            fontFamily: "'Space Mono', monospace",
            fontSize: 10,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            cursor: "pointer",
            borderRadius: 0,
          }}
        >
          ↓ download QR PNG
        </button>
      )}
    </div>
  );
}
